import { Box, Flex, Group, Paper, Progress, Stack, Text, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { FleetSummary } from "types/headsetReleaseTypes";
import { LatestBadge } from "./LatestBadge";
import { minorLineOf } from "./releaseFormat";

const LINE_COLORS = ["green", "yellow", "red", "grape"];
const isVersion = (version: string) => /^\d+\.\d+/.test(version);
const versionShade = (indexInLine: number) => Math.max(9 - indexInLine, 4);
const tabularNumbers = { fontVariantNumeric: "tabular-nums" } as const;

interface VersionLine {
  name: string;
  color: string;
  total: number;
  versions: { version: string; count: number }[];
}

export function linesBehindLatest(lines: VersionLine[], latestVersion?: string): VersionLine[] {
  if (!latestVersion) return [];
  const latestLine = minorLineOf(latestVersion);
  return lines.filter(
    (line) => line.name && line.name.localeCompare(latestLine, undefined, { numeric: true }) < 0,
  );
}

export function versionLines(summary: FleetSummary): VersionLine[] {
  const lines = new Map<string, { version: string; count: number }[]>();
  for (const entry of summary.versions) {
    const name = isVersion(entry.version) ? minorLineOf(entry.version) : "";
    lines.set(name, [...(lines.get(name) ?? []), entry]);
  }
  const names = [...lines.keys()]
    .filter(Boolean)
    .sort((left, right) => right.localeCompare(left, undefined, { numeric: true }));
  if (lines.has("")) names.push("");
  return names.map((name, index) => {
    const versions = lines.get(name) ?? [];
    return {
      name,
      color: name ? LINE_COLORS[Math.min(index, LINE_COLORS.length - 1)] : "gray",
      total: versions.reduce((sum, entry) => sum + entry.count, 0),
      versions,
    };
  });
}

interface FleetCardProps {
  summary: FleetSummary;
  latestVersion?: string;
}

export function FleetCard({ summary, latestVersion }: FleetCardProps) {
  const { t } = useTranslation();
  const lines = versionLines(summary);
  const largestVersionCount = Math.max(...summary.versions.map((entry) => entry.count), 1);
  const onLatest = summary.versions.find((entry) => entry.version === latestVersion)?.count ?? 0;
  const behindLines = linesBehindLatest(lines, latestVersion);
  const stats = [
    {
      value: summary.active ? `${Math.round((onLatest / summary.active) * 100)}%` : "-",
      label: t("headsetReleases.fleet.onLatest", {
        version: latestVersion ?? "-",
        count: onLatest,
      }),
      color: "green",
    },
    {
      value: behindLines.reduce((sum, line) => sum + line.total, 0),
      label: t("headsetReleases.fleet.olderLines", {
        lines: behindLines.map((line) => line.name).join(", ") || "-",
      }),
      color: "red",
    },
    {
      value: summary.selfUpdating,
      label: t("headsetReleases.fleet.selfUpdating"),
      color: undefined,
    },
    {
      value: summary.inactive,
      label: t("headsetReleases.fleet.inactive", { days: summary.activeWindowDays }),
      color: "dimmed",
    },
  ];

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={4}>{t("headsetReleases.fleet.title")}</Title>
          <Text size="sm" c="dimmed">
            {t("headsetReleases.fleet.active", {
              count: summary.active,
              days: summary.activeWindowDays,
            })}
          </Text>
        </Group>
        <Flex wrap="wrap" gap={28} align="flex-start">
          <Stack gap="sm" style={{ flex: "1 1 220px", maxWidth: 280 }}>
            {stats.map((stat) => (
              <Paper key={stat.label} withBorder p="sm" radius="md">
                <Text fz={24} fw={700} c={stat.color} style={tabularNumbers}>
                  {stat.value}
                </Text>
                <Text size="sm" c="dimmed">
                  {stat.label}
                </Text>
              </Paper>
            ))}
          </Stack>
          <Stack
            gap="lg"
            role="img"
            aria-label={t("headsetReleases.fleet.byVersion")}
            style={{ flex: "999 1 560px", minWidth: 0 }}
          >
            {lines.map((line) => (
              <Stack key={line.name || "unknown"} gap={6}>
                <Group
                  justify="space-between"
                  pb={4}
                  style={{ borderBottom: "2px solid var(--mantine-color-default-border)" }}
                >
                  <Group gap={8}>
                    <Box
                      w={12}
                      h={12}
                      bg={line.name ? `${line.color}.9` : "gray.6"}
                      style={{ borderRadius: 3 }}
                    />
                    <Text fw={700}>
                      {line.name ? `${line.name}.x` : t("headsetReleases.fleet.unknown")}
                    </Text>
                  </Group>
                  <Text fw={700} style={tabularNumbers}>
                    {line.total}
                  </Text>
                </Group>
                {line.versions.map((entry, index) => (
                  <Box
                    key={entry.version || "unknown"}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "150px minmax(0, 1fr) 44px",
                      gap: 12,
                      alignItems: "center",
                    }}
                  >
                    <Group gap={6} wrap="nowrap">
                      <Text size="sm" fw={600}>
                        {entry.version || t("headsetReleases.fleet.notReported")}
                      </Text>
                      {entry.version === latestVersion && <LatestBadge />}
                    </Group>
                    <Progress
                      value={(entry.count / largestVersionCount) * 100}
                      color={line.name ? `${line.color}.${versionShade(index)}` : "gray.6"}
                      size={14}
                      radius="sm"
                      aria-label={entry.version || t("headsetReleases.fleet.notReported")}
                    />
                    <Text size="sm" ta="right" style={tabularNumbers}>
                      {entry.count}
                    </Text>
                  </Box>
                ))}
              </Stack>
            ))}
          </Stack>
        </Flex>
      </Stack>
    </Paper>
  );
}
